import { ORG_CONFIG_OVERRIDES } from 'js/constants/config';
import { getFeatureFlagValue } from 'new/constants/featureFlags';
import { merge } from 'lodash';

/*
 * Structure for organization-specific configuration overrides
 * Example:
 * {
 *   "1": {  // org ID
 *     "source_config": {
 *       "group_1": {
 *         "overrides": {
 *           "force": {
 *             // These values will always override user settings
 *             "tasks.max": 16,
 *             "datasets.partitions": 32
 *           },
 *           "merge": {
 *             // These values will be merged with user settings
 *             "docker.instances": 4,
 *             "custom.setting": "value",
 *             "nested.config": {
 *               "key1": "value1",
 *               "key2": 123
 *             }
 *           },
 *           "array.setting": [1, 2, 3],
 *           "boolean.flag": true
 *         },
 *         "rules": {
 *           "connection_categories": ["files"],
 *           "flow_types": ["in_memory"]  // specific flow type
 *         }
 *       },
 *       "group_2": {
 *         "overrides": {
 *           // Different set of overrides
 *           "tasks.max": 8,
 *           "datasets.partitions": 16,
 *           "other.setting": "different value"
 *         },
 *         "rules": {
 *           "connection_categories": ["files", "apis", "databases"],
 *           "flow_types": ["*"]  // all flow types
 *         }
 *       }
 *     }
 *   }
 * }
 *
 * Rule Conflict Resolution:
 * - When multiple groups match the same org+connection+flow_type:
 * 1. Specific flow type matches take precedence over wildcard ("*") matches
 * 2. If specificity is the same, the first matching group in the config wins
 * 3. Configuration errors are logged to help flag setters identify issues
 */

interface GroupMatch {
  groupId: string;
  isWildcard: boolean;
}

/**
 * Gets merged org config overrides (env var + GrowthBook) for non-React contexts
 * GrowthBook values take precedence over environment variable values
 * @returns Merged configuration object
 */
function getMergedOrgConfigOverrides(): Record<string, any> {
  const growthBookOverrides = getFeatureFlagValue('UI_ORG_CONFIG_OVERRIDES');
  return merge({}, ORG_CONFIG_OVERRIDES, growthBookOverrides);
}

/**
 * Gets all matching groups for a given org, config type, connection category, and flow type
 * @param orgId - The organization ID
 * @param configType - The type of config ('source_config' or 'sink_config')
 * @param connectionCategory - The connection category
 * @param flowType - The flow type (undefined will only match wildcard rules)
 * @param overrides - The merged configuration overrides (env + GrowthBook)
 * @returns Array of matching groups with their match type
 */
function getAllMatchingGroups(
  orgId: string,
  configType: 'source_config' | 'sink_config',
  connectionCategory: string,
  flowType: string | undefined,
  overrides: Record<string, any>,
): GroupMatch[] {
  const groups = overrides[orgId]?.[configType] || {};

  return Object.entries(groups)
    .filter(([_, group]: [string, any]) => {
      const { rules } = group;
      if (!rules) return false;

      const categoryMatch =
        !rules.connection_categories || rules.connection_categories.includes(connectionCategory);
      const flowTypeMatch =
        !rules.flow_types ||
        rules.flow_types.includes('*') ||
        (flowType && rules.flow_types.includes(flowType));

      return categoryMatch && flowTypeMatch;
    })
    .map(([groupId, group]: [string, any]) => ({
      groupId,
      isWildcard: !group.rules.flow_types || group.rules.flow_types.includes('*'),
    }));
}

/**
 * Gets the applicable group for a given org, config type, connection category, and flow type
 * @param orgId - The organization ID
 * @param configType - The type of config ('source_config' or 'sink_config')
 * @param connectionCategory - The connection category
 * @param flowType - The flow type (undefined will only match wildcard rules)
 * @param overrides - The merged configuration overrides (env + GrowthBook)
 * @returns The group ID or null if no matching rule is found
 */
export function getApplicableGroup(
  orgId: string,
  configType: 'source_config' | 'sink_config',
  connectionCategory: string,
  flowType: string | undefined,
  overrides: Record<string, any>,
): string | null {
  const matches = getAllMatchingGroups(orgId, configType, connectionCategory, flowType, overrides);

  if (matches.length > 1) {
    // Log configuration error but maintain predictable behavior
    console.warn(
      `Configuration Error: Multiple matching groups found for org=${orgId}, ` +
        `config=${configType}, category=${connectionCategory}, flow=${flowType || 'undefined'}. ` +
        `Groups: ${matches.map((m) => m.groupId).join(', ')}. ` +
        `Using most specific match.`,
    );

    // First try to find a non-wildcard match
    const specificMatch = matches.find((m) => !m.isWildcard);
    if (specificMatch) {
      return specificMatch.groupId;
    }
  }

  // Return first match (whether it's the only match or first wildcard match)
  return matches[0]?.groupId || null;
}

/**
 * Checks if there are any overrides for a given org, config type, connection category, and flow type
 * @param orgId - The organization ID
 * @param configType - The type of config ('source_config' or 'sink_config')
 * @param connectionCategory - The connection category
 * @param flowType - The flow type (undefined will only match wildcard rules)
 * @param overrides - The merged configuration overrides (env + GrowthBook)
 * @returns boolean indicating if there are overrides
 */
export function hasOrgConfigOverride(
  orgId: string,
  configType: 'source_config' | 'sink_config',
  connectionCategory: string,
  flowType: string | undefined,
  overrides: Record<string, any>,
): boolean {
  const group = getApplicableGroup(orgId, configType, connectionCategory, flowType, overrides);
  if (!group) return false;

  return !!overrides[orgId]?.[configType]?.[group]?.overrides;
}

/**
 * Merges override values with user config based on the override structure
 * @param userConfig - The user's configuration
 * @param overrides - The override configuration
 * @returns Merged configuration
 */
function mergeOverrides(
  userConfig: Record<string, any>,
  overrides: Record<string, any>,
): Record<string, any> {
  const result = { ...userConfig };

  // Handle force overrides first - these always take precedence
  if (overrides.force) {
    Object.assign(result, overrides.force);
  }

  // Handle merge overrides - these are merged with user settings
  if (overrides.merge) {
    Object.entries(overrides.merge).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Deep merge for objects
        result[key] = {
          ...(result[key] || {}),
          ...value,
        };
      } else {
        // Simple assignment for non-objects
        result[key] = value;
      }
    });
  }

  // Handle any other overrides (legacy support)
  Object.entries(overrides).forEach(([key, value]) => {
    if (key !== 'force' && key !== 'merge') {
      result[key] = value;
    }
  });

  return result;
}

/**
 * Gets the overrides for a given org, config type, connection category, and flow type
 * @param orgId - The organization ID
 * @param configType - The type of config ('source_config' or 'sink_config')
 * @param connectionCategory - The connection category
 * @param flowType - The flow type (undefined will only match wildcard rules)
 * @param overrides - The merged configuration overrides (env + GrowthBook)
 * @returns The overrides object or null if no overrides exist. The overrides object can contain any key-value pairs.
 */
export function getOrgConfigOverrides(
  orgId: string,
  configType: 'source_config' | 'sink_config',
  connectionCategory: string,
  flowType: string | undefined,
): Record<string, any> | null {
  const mergedOverrides = getMergedOrgConfigOverrides();
  const group = getApplicableGroup(
    orgId,
    configType,
    connectionCategory,
    flowType,
    mergedOverrides,
  );
  if (!group) return null;

  return mergedOverrides[orgId]?.[configType]?.[group]?.overrides || null;
}

/**
 * Applies overrides to a user configuration
 * @param userConfig - The user's configuration
 * @param overrides - The override configuration
 * @returns The merged configuration
 */
export function applyOrgConfigOverrides(
  userConfig: Record<string, any>,
  overrides: Record<string, any> | null,
): Record<string, any> {
  if (!overrides) return userConfig;
  return mergeOverrides(userConfig, overrides);
}
